import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import type { AuthProvider, AuthSource, AuthType } from '../../user/type';
import LoginSignUpModal from '../../user/component/login-signup-modal/LoginSignUpModal';
import { useUser } from '@modules/user/hook/use-user';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';
import { getPostAuthDestination, hasFeature } from '~/modules/root';
import { trackAuthEvent } from '../../user/util/track-auth-event';
import { useTracker } from '@archipro-website/tracker';
import { AUTH_EVENTS } from '~/modules/root/hook/use-auth-event';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { handleAuthRedirect } from '~/modules/root/util/handle-auth-redirect';
import { useFeatureFlags } from '@modules/root';
import { hasNonPersonalBuildHubAccess } from '~/modules/user/util/owned-build-hubs';

interface AuthState {
    open: boolean;
    authSource: AuthSource;
    flow?: LoginFlow;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    disableCloseButton?: boolean;
    /** Override the main heading (defaults to "Welcome to ArchiPro"). */
    header?: string;
    subtitle?: string;
    message?: string;
    defaultPublicUser?: PublicUser;
    onCancel?: () => void;
    onConfirm?: () => void;
    onSuccess?: () => void;
    onAuthSuccess?: () => void;
}

interface AuthRedirectState extends Omit<AuthState, 'open'> {
    provider: AuthProvider;
    authType: AuthType;
    /**
     * The member's tracked-guest id, when the response that authenticated them
     * reported it. Supplying it is what lets the redirect leave without waiting
     * for the session to reappear on the client — see the effect below.
     */
    guestID?: number;
}

export const AuthContext = React.createContext<
    | {
          showAuthModal: (props: Omit<AuthState, 'open'>) => void;
          hideAuthModal: () => void;
          authRedirect: (props: AuthRedirectState) => void;
          /** True while this context's LoginSignUpModal is rendered. */
          authModalOpen: boolean;
      }
    | undefined
>(undefined);

export const AuthContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [state, setState] = useState<AuthState>({
        authSource: 'topNav',
        open: false,
    });

    const [redirectState, setRedirectState] =
        useState<AuthRedirectState | null>(null);

    const location = useLocation();
    const user = useUser();
    const navigation = useNavigation();
    const tracker = useTracker();
    const { isFirefox } = useDeviceBrowserContext();
    const featureFlags = useFeatureFlags();
    const BuildHubFeatureEnabled = hasFeature(featureFlags, 'buildhub');

    const showModal = useCallback(
        (props: Omit<AuthState, 'open'>) => {
            setState({ ...props, open: true });
            // This modal path bypasses useSetLoginModal, so log the
            // prompt-shown funnel step here (covers post-enquiry login/signup
            // and every other showAuthModal surface).
            // Source and ExtraData are the only custom keys the warehouse
            // pipeline persists; the flat keys serve network QA.
            tracker.log('LoginPromptShown', {
                url: new URL(window.location.href),
                data: {
                    authSource: props.authSource,
                    ...(props.flow && { flow: props.flow }),
                    Source: props.authSource,
                    ExtraData: JSON.stringify({
                        authSource: props.authSource,
                        ...(props.flow && { flow: props.flow }),
                    }),
                },
            });
        },
        [tracker],
    );

    const hideModal = useCallback(() => {
        setState({ authSource: 'topNav', open: false });
    }, []);

    const redirect = useCallback((props: AuthRedirectState) => {
        setRedirectState(props);
    }, []);

    useEffect(() => {
        if (!redirectState || navigation.state === 'loading') return;

        const { pathname, search } = location;
        const {
            redirect,
            allowExternalUrls,
            authSource,
            provider,
            authType,
            skipPostSignUp,
            guestID: reportedGuestID,
        } = redirectState;

        const originSearchParams = new URLSearchParams(search);
        const encodedRedirect = originSearchParams.get('encodedRedirect');

        /**
         * Whether this redirect has to see the session on the client first.
         *
         * It becomes `Me` here only once React Router has revalidated every
         * loader — and this redirect ends in a full page load that will run
         * them all again, so waiting buys a round trip's delay and nothing
         * else. It is worth paying only for what genuinely needs the member:
         * the Build Hub onboarding branch of the destination, and the guest id
         * on the auth event, which callers can instead report from the response
         * that authenticated them.
         */
        const needsMember =
            reportedGuestID === undefined ||
            (!skipPostSignUp && !encodedRedirect && BuildHubFeatureEnabled);
        if (needsMember && user.__typename !== 'Me') return;

        const member = user.__typename === 'Me' ? user : undefined;
        const guestID = reportedGuestID ?? member?.TrackedGuest?.ID ?? 0;
        // Treat collaborator membership on a non-personal BuildHub as project access
        // so a freshly-signed-up HDE guest is not funnelled into /app/create-project
        // when they accept an invite. The helper consults the server-side
        // HasNonPersonalBuildHubAccess boolean and falls back to OwnedBuildHubs to
        // keep the legacy behaviour if the field is missing from a stale Apollo cache.
        //
        // Only read on the branch `needsMember` covers, so the fallbacks here
        // stand in for a member the destination will not consult.
        const hasBuildHubs = member
            ? hasNonPersonalBuildHubAccess(member)
            : false;
        const onboardingDismissedAt = (
            member as { OnboardingDismissed?: string | null } | undefined
        )?.OnboardingDismissed;
        const finalDestination = getPostAuthDestination({
            pathname,
            search,
            redirect,
            allowExternalUrls,
            skipPostSignUp,
            encodedRedirect,
            buildHubFeatureEnabled: BuildHubFeatureEnabled,
            hasBuildHubs,
            onboardingDismissedAt,
        });
        const redirectURL = async () => {
            await trackAuthEvent({
                tracker,
                event: AUTH_EVENTS[authType],
                provider,
                authSource,
                guestID,
            });

            // Straight to the destination, including from `/login`. Relaying
            // through the login route instead —
            // `/login?redirect=<destination>` — asked its loader to repeat a
            // redirect already resolved above, and that loader greets an
            // authenticated visitor with "You are already logged in": a toast
            // that reads as a rebuke to the one person who just logged in,
            // shown on whatever page they land on. The session cookie is set
            // by the auth response, so the destination can be requested
            // directly.
            await handleAuthRedirect({
                newURL: finalDestination,
                authSource,
                isFirefox,
            });
        };

        setRedirectState(null);
        redirectURL();
    }, [
        location,
        setState,
        tracker,
        user,
        navigation.state,
        redirectState,
        isFirefox,
        BuildHubFeatureEnabled,
    ]);

    return (
        <AuthContext.Provider
            value={{
                showAuthModal: showModal,
                hideAuthModal: hideModal,
                authRedirect: redirect,
                authModalOpen: state.open,
            }}
        >
            {children}
            {/* Need to re-render modal to apply defaultPublicUser changes. */}
            {/* TODO: Fix this so that we can always render the modal. */}
            {state.open && (
                <LoginSignUpModal
                    login={{
                        onOpen: () => showModal({ ...state }),
                        onCancel: hideModal,
                        onConfirm: hideModal,
                        onSuccess: hideModal,
                        onAuthSuccess: hideModal,
                        flow: null,
                        ...state,
                    }}
                    forgotPassword={{
                        open: state.open,
                        onCancel: hideModal,
                    }}
                />
            )}
        </AuthContext.Provider>
    );
};

/**
 * Hook to provide functions to show and hide the login/signup modal. Must
 * be used within the `AuthContext.Provider`.
 */
export function useAuth() {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('Cannot be used outside AuthContext');
    }
    return context;
}
